import React, { useState, useEffect, useRef } from 'react';
import { colorFromName } from '../lib/palette';

/**
 * Derive a clean domain from a website string.
 */
export const domainFor = (website) => {
  if (!website) return null;
  const cleaned = website
    .trim()
    .replace(/^https?:\/\//i, '')
    .replace(/^www\./i, '')
    .split(/[/?#]/)[0]
    .trim();
  return cleaned || null;
};

export const initialsOf = (name) =>
  (name || '')
    .split(/\s+/)
    .filter(Boolean)
    .map((w) => w[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();

const firstLetterOf = (name) =>
  (name || '').trim().charAt(0).toUpperCase() || '?';

// Progressive image sources: uploaded logo (if any) → icon.horse (keyless,
// resolves logos for niche domains the favicon services miss) → unavatar →
// DuckDuckGo. Google favicon is intentionally omitted: it returns a generic
// globe image with a 200 status, which breaks the onError fallback (showing a
// globe instead of the initials). Clearbit's free API was shut down.
const sourcesFor = (startup) => {
  const list = [];
  if (startup?.logo_url) list.push(startup.logo_url);
  const domain = domainFor(startup?.website);
  if (domain) {
    list.push(`https://icon.horse/icon/${domain}`);
    list.push(`https://unavatar.io/${domain}?fallback=false`);
    list.push(`https://icons.duckduckgo.com/ip3/${domain}.ico`);
  }
  return list;
};

const Logo = ({ startup, size = 40, rounded = 'full', className = '' }) => {
  const sources = sourcesFor(startup);
  const [idx, setIdx] = useState(0);
  const [loaded, setLoaded] = useState(false);
  const [failed, setFailed] = useState(false);
  const imgRef = useRef(null);

  useEffect(() => {
    setIdx(0);
    setLoaded(false);
    setFailed(false);
  }, [startup?.id, startup?.website, startup?.logo_url]);

  const currentSrc = sources[idx];
  // Instant/cached images (e.g. base64 data URLs) may fire `load` before the
  // React handler attaches — detect that case so the logo isn't stuck behind
  // the loading placeholder.
  useEffect(() => {
    const el = imgRef.current;
    if (el && el.complete && el.naturalWidth > 0) setLoaded(true);
  }, [currentSrc]);

  const handleError = () => {
    if (idx + 1 < sources.length) {
      setIdx(idx + 1);
      setLoaded(false);
    } else {
      setFailed(true);
    }
  };

  const radiusClass = rounded === 'full' ? 'rounded-full' : 'rounded-md';
  const bgColor = startup?.color || colorFromName(startup?.name);
  const letter = firstLetterOf(startup?.name);
  const fontSize = Math.max(12, Math.round(size * 0.44));

  // No sources OR all failed → colored circle with first letter.
  if (failed || sources.length === 0) {
    return (
      <div
        className={`flex items-center justify-center text-white font-bold flex-shrink-0 ${radiusClass} ${className}`}
        style={{
          width: size,
          height: size,
          background: bgColor,
          fontSize,
          letterSpacing: '-0.02em',
        }}
        aria-label={startup?.name}
      >
        {letter}
      </div>
    );
  }

  return (
    <div
      className={`relative flex items-center justify-center flex-shrink-0 overflow-hidden border border-slate-100 bg-white ${radiusClass} ${className}`}
      style={{ width: size, height: size }}
      aria-label={startup?.name}
    >
      {!loaded && (
        <div
          className="absolute inset-0 flex items-center justify-center text-white font-bold"
          style={{
            background: bgColor,
            fontSize,
            letterSpacing: '-0.02em',
          }}
        >
          {letter}
        </div>
      )}
      <img
        key={sources[idx]}
        ref={imgRef}
        src={sources[idx]}
        alt={startup?.name || ''}
        loading="lazy"
        referrerPolicy="no-referrer"
        onLoad={() => setLoaded(true)}
        onError={handleError}
        className="relative w-full h-full object-contain p-[3px]"
        style={{ opacity: loaded ? 1 : 0, transition: 'opacity 180ms ease-out' }}
      />
    </div>
  );
};

export default Logo;
